import React, { useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  Strikethrough,
  List, 
  ListOrdered, 
  AlignLeft, 
  AlignCenter, 
  AlignRight,
  Link as LinkIcon,
  Heading1,
  Heading2,
  Quote,
  Undo,
  Redo
} from 'lucide-react';

// Estilos del editor
const editorStyles = `
  .tiptap-editor-wrapper {
    border: 1px solid #d0d0d0;
    border-radius: 8px;
    overflow: hidden;
    background: white;
  }
  
  .tiptap-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    padding: 8px;
    background: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
  }
  
  .tiptap-toolbar-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    color: #555;
    transition: all 0.15s ease;
  }
  
  .tiptap-toolbar-btn:hover {
    background: #e0e0e0;
    color: #333;
  }
  
  .tiptap-toolbar-btn.is-active {
    background: #1976D2;
    color: white;
  }
  
  .tiptap-toolbar-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  
  .tiptap-toolbar-divider {
    width: 1px;
    height: 24px;
    background: #d0d0d0;
    margin: 0 4px;
    align-self: center;
  }
  
  .tiptap-editor-content {
    padding: 12px 16px;
    min-height: 150px;
    max-height: 400px;
    overflow-y: auto;
  }
  
  .tiptap-editor-content .ProseMirror {
    outline: none;
    min-height: 120px;
  }
  
  .tiptap-editor-content .ProseMirror p {
    margin: 0 0 0.5em 0;
  }
  
  .tiptap-editor-content .ProseMirror p:last-child {
    margin-bottom: 0;
  }
  
  .tiptap-editor-content .ProseMirror ul,
  .tiptap-editor-content .ProseMirror ol {
    padding-left: 1.5em;
    margin: 0.5em 0;
  }
  
  .tiptap-editor-content .ProseMirror li {
    margin: 0.25em 0;
  }
  
  .tiptap-editor-content .ProseMirror h1 {
    font-size: 1.75em;
    font-weight: 700;
    margin: 0.5em 0;
  }
  
  .tiptap-editor-content .ProseMirror h2 {
    font-size: 1.4em;
    font-weight: 700;
    margin: 0.5em 0;
  }
  
  .tiptap-editor-content .ProseMirror blockquote {
    border-left: 4px solid #1976D2;
    padding-left: 1em;
    margin: 0.5em 0;
    color: #555;
    font-style: italic;
  }
  
  .tiptap-editor-content .ProseMirror a {
    color: #1976D2;
    text-decoration: underline;
    cursor: pointer;
  }
  
  .tiptap-editor-content .ProseMirror .is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    color: #aaa;
    pointer-events: none;
    height: 0;
    float: left;
  }
  
  /* Estilos para contenido renderizado (display) */
  .rich-text-content {
    line-height: 1.6;
    color: #333;
  }
  
  .rich-text-content p {
    margin: 0 0 0.5em 0;
  }
  
  .rich-text-content ul, .rich-text-content ol {
    padding-left: 1.5em;
    margin: 0.5em 0;
  }
  
  .rich-text-content a {
    color: #1976D2;
    text-decoration: underline;
  }
  
  .rich-text-content h1 {
    font-size: 1.75em;
    font-weight: 700;
    margin: 0.5em 0;
  }
  
  .rich-text-content h2 {
    font-size: 1.4em;
    font-weight: 700;
    margin: 0.5em 0;
  }
  
  .rich-text-content blockquote {
    border-left: 4px solid #1976D2;
    padding-left: 1em;
    margin: 0.5em 0;
    color: #555;
    font-style: italic;
  }
`;

// Insertar estilos en el documento
if (typeof document !== 'undefined') {
  const styleId = 'tiptap-editor-styles';
  if (!document.getElementById(styleId)) {
    const styleSheet = document.createElement('style');
    styleSheet.id = styleId;
    styleSheet.textContent = editorStyles;
    document.head.appendChild(styleSheet);
  }
}

// Botón de toolbar
const ToolbarButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`tiptap-toolbar-btn ${isActive ? 'is-active' : ''}`}
    title={title}
  >
    {children}
  </button>
);

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = 'Escriba aquí...',
  minHeight = '150px',
  readOnly = false
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2]
        }
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer'
        }
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      TextStyle,
      Color
    ],
    content: value || '',
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
    editorProps: {
      attributes: {
        'data-placeholder': placeholder
      }
    }
  });

  // Función para agregar enlace
  const setLink = useCallback(() => {
    if (!editor) return;
    
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL del enlace:', previousUrl || 'https://');
    
    if (url === null) return;
    
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) {
    return null;
  }

  if (readOnly) {
    return (
      <div 
        className="rich-text-content"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value || '') }}
        style={{ 
          minHeight,
          padding: '0.75rem',
          border: '1px solid #e0e0e0',
          borderRadius: '6px',
          backgroundColor: '#fafafa'
        }}
      />
    );
  }

  return (
    <div className="tiptap-editor-wrapper" style={{ minHeight }}>
      {/* Toolbar */}
      <div className="tiptap-toolbar">
        {/* Undo/Redo */}
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Deshacer"
        >
          <Undo size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Rehacer"
        >
          <Redo size={16} />
        </ToolbarButton>
        
        <div className="tiptap-toolbar-divider" />
        
        {/* Headings */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Título 1"
        >
          <Heading1 size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Título 2"
        >
          <Heading2 size={16} />
        </ToolbarButton>
        
        <div className="tiptap-toolbar-divider" />
        
        {/* Text formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Negrita (Ctrl+B)"
        >
          <Bold size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Cursiva (Ctrl+I)"
        >
          <Italic size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Subrayado (Ctrl+U)"
        >
          <UnderlineIcon size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Tachado"
        >
          <Strikethrough size={16} />
        </ToolbarButton>
        
        <div className="tiptap-toolbar-divider" />
        
        {/* Lists */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Lista con viñetas"
        >
          <List size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Lista numerada"
        >
          <ListOrdered size={16} />
        </ToolbarButton>
        
        <div className="tiptap-toolbar-divider" />
        
        {/* Alignment */}
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Alinear izquierda"
        >
          <AlignLeft size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Centrar"
        >
          <AlignCenter size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Alinear derecha"
        >
          <AlignRight size={16} />
        </ToolbarButton>
        
        <div className="tiptap-toolbar-divider" />
        
        {/* Link & Quote */}
        <ToolbarButton
          onClick={setLink}
          isActive={editor.isActive('link')}
          title="Insertar enlace"
        >
          <LinkIcon size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Cita"
        >
          <Quote size={16} />
        </ToolbarButton>
      </div>
      
      {/* Editor content */}
      <div className="tiptap-editor-content" style={{ minHeight }}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

// Componente para renderizar contenido HTML de manera segura
export const RichTextDisplay = ({ content, className = '' }) => {
  if (!content) return null;
  
  return (
    <div 
      className={`rich-text-content ${className}`}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
    />
  );
};

export default RichTextEditor;
